import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    MenuItem,
    Select,
    FormControl
} from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const api = axios.create({ withCredentials: true });

const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' }
];

function EventRequest(props) {
    const { getAccessTokenSilently } = useAuth0();
    const { current_broker, current_user } = useOutletContext();
    const [formData, setFormData] = useState({
        event_name: '',
        event_date: '',
        event_time: '',
        venue: '',
        city: '',
        state: '',
        reference_url: '',
        notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [brokerName, setBrokerName] = useState('Loading...');

    // Fetch broker name when component mounts
    React.useEffect(() => {
        const fetchBrokerName = async () => {
            if (current_broker === 'all') {
                setBrokerName('All Brokers');
                return;
            }

            try {
                const token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0AUDIENCE,
                    scope: process.env.REACT_APP_AUTH0SCOPE
                });

                // Query the database to get client_broker_name for this buyer_broker_id
                const response = await api.get(
                    `${process.env.REACT_APP_BASEURL}/api/feature-request/broker-name/${current_broker}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setBrokerName(response.data.client_broker_name || 'Unknown Broker');
            } catch (error) {
                console.error('Error fetching broker name:', error);
                setBrokerName('Unknown Broker');
            }
        };

        fetchBrokerName();
    }, [current_broker, getAccessTokenSilently]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form - all fields are required
        if (!formData.event_name.trim() || 
            !formData.event_date || 
            !formData.event_time.trim() ||
            !formData.venue.trim() ||
            !formData.city.trim() ||
            !formData.state.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        // Ensure broker name is loaded
        if (brokerName === 'Loading...') {
            toast.error('Please wait for broker information to load');
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const submissionData = {
                user_email: current_user.email,
                user_name: `${current_user.fname} ${current_user.lname}`,
                vendor: brokerName,
                ...formData
            };

            await api.post(
                `${process.env.REACT_APP_BASEURL}/api/event-request/submit`,
                submissionData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Event request submitted successfully!');
            
            // Reset form
            setFormData({
                event_name: '',
                event_date: '',
                event_time: '',
                venue: '',
                city: '',
                state: '',
                reference_url: '',
                notes: ''
            });

            // Close the dialog
            if (props.onClose) {
                props.onClose();
            }

        } catch (error) {
            console.error('Error submitting event request:', error);
            toast.error('Failed to submit event request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" component="h1" gutterBottom color="primary">
                    Event Request Form
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Request a new event to be added to the system. All fields are required.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Submitting as:</strong> {current_user.fname} {current_user.lname} ({current_user.email})
                    <br />
                    <strong>Vendor:</strong> {brokerName}
                </Alert>

                <form onSubmit={handleSubmit}>
                    <List component={Paper} variant="outlined">
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Event Name *
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter the event name..."
                                        value={formData.event_name}
                                        onChange={(e) => handleInputChange('event_name', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Event Date *
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        type="date"
                                        variant="outlined"
                                        value={formData.event_date}
                                        onChange={(e) => handleInputChange('event_date', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Event Time *
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        type="time"
                                        variant="outlined"
                                        value={formData.event_time}
                                        onChange={(e) => handleInputChange('event_time', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Venue *
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter the venue name..."
                                        value={formData.venue}
                                        onChange={(e) => handleInputChange('venue', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        City *
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter the city..."
                                        value={formData.city}
                                        onChange={(e) => handleInputChange('city', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        State *
                                    </Typography>
                                }
                                secondary={
                                    <FormControl fullWidth sx={{ mt: 1 }} required>
                                        <Select
                                            value={formData.state}
                                            onChange={(e) => handleInputChange('state', e.target.value)}
                                            disabled={isSubmitting}
                                            displayEmpty
                                        >
                                            <MenuItem value="" disabled>
                                                Select a state...
                                            </MenuItem>
                                            {US_STATES.map((state) => (
                                                <MenuItem key={state.value} value={state.value}>
                                                    {state.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Reference URL
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter a reference URL (optional)..."
                                        value={formData.reference_url}
                                        onChange={(e) => handleInputChange('reference_url', e.target.value)}
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Notes
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="Enter any additional notes (optional)..."
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                    </List>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || brokerName === 'Loading...'}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Event Request'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
}

export default EventRequest;
